import { http } from '@/lib/http';

/**
 * Request body for `POST /businesses` — mirrors
 * `controller.createBusinessRequest` in the vizitpro-api swagger spec.
 * `name` is required (min length 1); the rest are optional.
 */
export interface CreateBusinessRequest {
  name: string;
  business_type_id?: string;
  address?: string;
  phone?: string;
}

/** Mirrors `repo.Business` in the vizitpro-api swagger spec. */
export interface BusinessDto {
  id: string;
  name: string;
  business_type_id: string | null;
  address: string | null;
  phone: string | null;
  tier_id: string | null;
  billing_cycle: string | null;
  default_language: string | null;
  logo_url: string | null;
  subdomain: string | null;
  created_at: string;
  updated_at: string;
  /**
   * Stripe billing linkage. `stripe_customer_id`/`stripe_subscription_id`
   * are null until the owner completes Checkout at least once. All four
   * fields are read-only from the frontend — they only ever change via the
   * backend's Stripe webhook.
   */
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  subscription_status: 'trialing' | 'active' | 'past_due' | 'canceled';
  trial_ends_at: string | null;
  /**
   * Stripe Connect linkage for invoice card payments — separate from the
   * platform subscription billing above. `stripe_connect_account_id` is null
   * until the owner starts onboarding; `stripe_connect_charges_enabled`
   * mirrors the connected account's `charges_enabled`, kept in sync from the
   * Connect `account.updated` webhook. Both are read-only from the frontend.
   */
  stripe_connect_account_id: string | null;
  stripe_connect_charges_enabled: boolean;
}

export function businessQueryKey(businessId: string) {
  return ['businesses', businessId] as const;
}

/**
 * Calls `POST /businesses`. Creates a business and adds the caller as its
 * owner.
 */
export async function createBusiness(
  body: CreateBusinessRequest,
): Promise<BusinessDto> {
  const { data } = await http.post<BusinessDto>('/businesses', body);
  return data;
}

/** Calls `GET /businesses/{id}`. Returns a business owned by the caller. */
export async function getBusiness(businessId: string): Promise<BusinessDto> {
  const { data } = await http.get<BusinessDto>(`/businesses/${businessId}`);
  return data;
}

/**
 * Request body for `PATCH /businesses/{id}` — mirrors
 * `controller.updateBusinessRequest` in the vizitpro-api swagger spec. All
 * fields optional. Does not carry `tier_id` — subscription tier is set via
 * `POST /businesses/{id}/tier` (see `setBusinessTier` below) instead.
 */
export interface UpdateBusinessRequest {
  name?: string;
  business_type_id?: string;
  address?: string;
  phone?: string;
  logo_url?: string;
  default_language?: 'en' | 'es';
}

/** Calls `PATCH /businesses/{id}`. */
export async function updateBusiness(
  id: string,
  body: UpdateBusinessRequest,
): Promise<BusinessDto> {
  const { data } = await http.patch<BusinessDto>(`/businesses/${id}`, body);
  return data;
}

/**
 * Calls `POST /businesses/{id}/tier`. Changes a business's subscription
 * tier; owner-only, no payment gate yet. `createBusinessRequest` has no
 * tier field, so tier selection during onboarding is persisted via this
 * call, after creation.
 */
export async function setBusinessTier(
  id: string,
  tierId: string,
): Promise<BusinessDto> {
  const { data } = await http.post<BusinessDto>(`/businesses/${id}/tier`, {
    tier_id: tierId,
  });
  return data;
}
